import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import { useActiveProductSizes } from "../hooks/useProductChanges";

interface ProductSizeSelectorProps {
	value: number | null;
	onChange: (value: number | null) => void;
	label?: string;
	fullWidth?: boolean;
	size?: "small" | "medium";
	required?: boolean;
	error?: boolean;
	helperText?: string;
	/** Adds an "All" option. The clear button only shows where an empty value was allowed before (optional fields). */
	includeAll?: boolean;
	disabled?: boolean;
	sx?: SxProps<Theme>;
}

type Option = { Id: number; Name: string };

const ALL_OPTION: Option = { Id: 0, Name: "All" };

export const ProductSizeSelector = ({
	value,
	onChange,
	label = "Product",
	fullWidth = false,
	size = "small",
	required = false,
	error = false,
	helperText,
	includeAll = false,
	disabled = false,
	sx,
}: ProductSizeSelectorProps) => {
	const { data: items = [], isLoading } = useActiveProductSizes();
	const options: Option[] = includeAll ? [ALL_OPTION, ...items] : items;
	const selected =
		options.find((option) => option.Id === value) ??
		(includeAll ? ALL_OPTION : null);

	return (
		<Autocomplete
			options={options}
			value={selected}
			size={size}
			fullWidth={fullWidth}
			disabled={disabled || isLoading}
			disableClearable={includeAll || required}
			autoHighlight
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, current) => option.Id === current.Id}
			onChange={(_event, option) =>
				onChange(!option || option.Id === ALL_OPTION.Id ? null : option.Id)
			}
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			// The list opens as wide as its longest option instead of wrapping it.
			slotProps={{ popper: { sx: { minWidth: "fit-content" } } }}
			sx={{ minWidth: 120, ...sx } as SxProps<Theme>}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					error={error}
					helperText={helperText}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
			)}
		/>
	);
};
